// Iter58 Part B — Employee Recurring Payroll Items (allowances + deductions)
// Renders on the Employee Profile page. Templates persist across all payroll
// periods where the employee is in scope; `manual_overrides` on per-period
// lines do NOT feed back here.
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Pause, Play } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import DateInput from "./DateInput";

const EMPTY = {
    kind: "allowance",
    label: "",
    amount: "",
    frequency: "per_period",
    start_date: new Date().toISOString().slice(0, 10),
    end_date: "",
    remaining_installments: "",
    auto_expire_on_zero: true,
    notes: "",
};

export default function RecurringPayrollItems({ employeeId, canEdit }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialog, setDialog] = useState(null); // { mode: "create"|"edit", item }

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/employees/${employeeId}/recurring-items`);
            setItems(data || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { if (employeeId) load(); /* eslint-disable-next-line */ }, [employeeId]);

    const submit = async (form) => {
        try {
            const payload = {
                kind: form.kind,
                label: form.label,
                amount: Number(form.amount),
                frequency: form.frequency,
                start_date: form.start_date,
                end_date: form.end_date || null,
                remaining_installments: form.remaining_installments === "" ? null : Number(form.remaining_installments),
                auto_expire_on_zero: !!form.auto_expire_on_zero,
                notes: form.notes || null,
                status: form.status || "active",
            };
            if (dialog.mode === "create") {
                await api.post(`/employees/${employeeId}/recurring-items`, payload);
                toast.success("Recurring item created");
            } else {
                // PATCH: strip fields that can't change (kind is immutable)
                const { kind, ...rest } = payload;   // eslint-disable-line no-unused-vars
                await api.patch(`/recurring-items/${dialog.item.id}`, rest);
                toast.success("Recurring item updated");
            }
            setDialog(null);
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Remove this recurring item? Past applied amounts remain on approved payroll periods.")) return;
        try {
            await api.delete(`/recurring-items/${id}`);
            toast.success("Removed");
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const togglePause = async (item) => {
        try {
            await api.patch(`/recurring-items/${item.id}`, {
                status: item.status === "active" ? "paused" : "active",
            });
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const active = items.filter((i) => i.status !== "completed" && !i.deleted_at);
    const completed = items.filter((i) => i.status === "completed");

    return (
        <div className="asl-card p-5 space-y-4" data-testid="recurring-payroll-items">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-sm font-semibold uppercase tracking-wider asl-navy-text">Recurring Payroll Items</h3>
                    <p className="text-xs text-[color:var(--asl-muted)] mt-1">
                        Templates auto-applied to every payroll period this employee is in.
                        Per-period line edits (pencil icon) don't affect templates.
                    </p>
                </div>
                {canEdit && (
                    <div className="flex gap-2">
                        <Button size="sm" variant="outline" data-testid="recurring-add-allowance"
                            onClick={() => setDialog({ mode: "create", item: { ...EMPTY, kind: "allowance" } })}>
                            <Plus className="h-3.5 w-3.5 mr-1" /> Add allowance
                        </Button>
                        <Button size="sm" className="asl-navy-bg text-white" data-testid="recurring-add-deduction"
                            onClick={() => setDialog({ mode: "create", item: { ...EMPTY, kind: "deduction" } })}>
                            <Plus className="h-3.5 w-3.5 mr-1" /> Add deduction
                        </Button>
                    </div>
                )}
            </div>

            {loading ? (
                <div className="text-xs text-[color:var(--asl-muted)]">Loading…</div>
            ) : active.length === 0 && completed.length === 0 ? (
                <div className="text-xs text-[color:var(--asl-muted)] italic" data-testid="recurring-empty">
                    No recurring items yet.
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="recurring-table">
                        <thead className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">
                            <tr>
                                <th className="text-left px-3 py-2">Kind</th>
                                <th className="text-left px-3 py-2">Label</th>
                                <th className="text-right px-3 py-2">Amount</th>
                                <th className="text-left px-3 py-2">Frequency</th>
                                <th className="text-left px-3 py-2">Window</th>
                                <th className="text-right px-3 py-2">Remaining</th>
                                <th className="text-left px-3 py-2">Status</th>
                                <th className="text-right px-3 py-2"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {[...active, ...completed].map((i) => (
                                <tr key={i.id} className="border-t" data-testid={`recurring-row-${i.id}`}>
                                    <td className="px-3 py-2">
                                        <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full
                                            ${i.kind === "allowance" ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}>
                                            {i.kind}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2">{i.label}</td>
                                    <td className="px-3 py-2 mono text-right">${Number(i.amount || 0).toFixed(2)}</td>
                                    <td className="px-3 py-2 text-xs">{i.frequency}</td>
                                    <td className="px-3 py-2 text-xs mono">
                                        {i.start_date} → {i.end_date || "∞"}
                                    </td>
                                    <td className="px-3 py-2 mono text-right text-xs">
                                        {i.remaining_installments == null ? "—" : i.remaining_installments}
                                    </td>
                                    <td className="px-3 py-2">
                                        <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full
                                            ${i.status === "active" ? "bg-blue-100 text-blue-800"
                                                : i.status === "paused" ? "bg-amber-100 text-amber-900"
                                                    : "bg-gray-200 text-gray-700"}`}>
                                            {i.status}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2 text-right space-x-1">
                                        {canEdit && i.status !== "completed" && (
                                            <>
                                                <Button size="icon" variant="ghost" data-testid={`recurring-edit-${i.id}`}
                                                    onClick={() => setDialog({ mode: "edit", item: i })}>
                                                    <Pencil className="h-3.5 w-3.5" />
                                                </Button>
                                                <Button size="icon" variant="ghost" data-testid={`recurring-toggle-${i.id}`}
                                                    onClick={() => togglePause(i)}>
                                                    {i.status === "active"
                                                        ? <Pause className="h-3.5 w-3.5" />
                                                        : <Play className="h-3.5 w-3.5" />}
                                                </Button>
                                                <Button size="icon" variant="ghost" className="text-rose-600" data-testid={`recurring-delete-${i.id}`}
                                                    onClick={() => remove(i.id)}>
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </Button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <RecurringItemDialog
                dialog={dialog}
                onClose={() => setDialog(null)}
                onSubmit={submit}
            />
        </div>
    );
}

function RecurringItemDialog({ dialog, onClose, onSubmit }) {
    const [form, setForm] = useState(EMPTY);
    useEffect(() => {
        if (!dialog) return;
        if (dialog.mode === "edit") {
            setForm({
                kind: dialog.item.kind,
                label: dialog.item.label || "",
                amount: dialog.item.amount ?? "",
                frequency: dialog.item.frequency || "per_period",
                start_date: dialog.item.start_date || "",
                end_date: dialog.item.end_date || "",
                remaining_installments: dialog.item.remaining_installments ?? "",
                auto_expire_on_zero: dialog.item.auto_expire_on_zero ?? true,
                notes: dialog.item.notes || "",
                status: dialog.item.status || "active",
            });
        } else {
            setForm({ ...EMPTY, kind: dialog.item.kind });
        }
    }, [dialog]);

    if (!dialog) return null;
    const isEdit = dialog.mode === "edit";
    const isAllowance = form.kind === "allowance";

    return (
        <Dialog open={!!dialog} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-md" data-testid="recurring-dialog">
                <DialogHeader>
                    <DialogTitle>
                        {isEdit ? "Edit" : "New"} {isAllowance ? "Allowance" : "Deduction"}
                    </DialogTitle>
                </DialogHeader>
                <form onSubmit={(e) => { e.preventDefault(); onSubmit(form); }} className="space-y-3">
                    <div className="space-y-1">
                        <Label className="text-xs uppercase">Label *</Label>
                        <Input required value={form.label} placeholder={isAllowance ? "e.g. Housing Allowance" : "e.g. Loan Repayment"}
                            onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="recurring-label" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                            <Label className="text-xs uppercase">Amount *</Label>
                            <Input required type="number" step="0.01" min="0" value={form.amount}
                                onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="recurring-amount" />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs uppercase">Frequency</Label>
                            <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                                <SelectTrigger data-testid="recurring-frequency"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="per_period">Per period</SelectItem>
                                    <SelectItem value="per_month">Per month</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs uppercase">Start date *</Label>
                            <DateInput required value={form.start_date}
                                onChange={(v) => setForm({ ...form, start_date: v })} data-testid="recurring-start" />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs uppercase">End date (optional)</Label>
                            <DateInput value={form.end_date}
                                onChange={(v) => setForm({ ...form, end_date: v })} data-testid="recurring-end" />
                        </div>
                        {!isAllowance && (
                            <>
                                <div className="space-y-1 col-span-2">
                                    <Label className="text-xs uppercase">Remaining installments (leave blank for ongoing)</Label>
                                    <Input type="number" min="0" placeholder="ongoing" value={form.remaining_installments}
                                        onChange={(e) => setForm({ ...form, remaining_installments: e.target.value })} data-testid="recurring-installments" />
                                </div>
                                <div className="col-span-2 flex items-center gap-2 text-xs">
                                    <input type="checkbox" id="rec-auto-expire" checked={!!form.auto_expire_on_zero}
                                        onChange={(e) => setForm({ ...form, auto_expire_on_zero: e.target.checked })} data-testid="recurring-auto-expire" />
                                    <label htmlFor="rec-auto-expire" className="cursor-pointer">
                                        Auto-complete when installments reach 0
                                    </label>
                                </div>
                            </>
                        )}
                    </div>
                    <div className="space-y-1">
                        <Label className="text-xs uppercase">Notes</Label>
                        <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
                        <Button type="submit" className="asl-navy-bg text-white" data-testid="recurring-submit">
                            {isEdit ? "Save changes" : "Create"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
